import { FC, useMemo, useState } from 'react';
import { useAtom, useSetAtom } from 'jotai';
import {
  Button,
  DataTable,
  InlineNotification,
  Loading,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
  TableToolbar,
  TableToolbarContent,
  TableToolbarSearch,
} from '@carbon/react';
import { CheckmarkOutline, Close, Edit } from '@carbon/react/icons';
import { formatResource } from 'utils/misc';
import { StatusTag, type PartyStatus } from 'components/status-tag';
import type { WellSectionStatus } from './types';
import {
  confirmIdentificationAtom,
  createIdentificationResourceAtom,
  identificationUrl,
  revertIdentificationAtom,
} from './atoms/identification';
import { IdentificationEditPanel } from './identification-edit-panel';
import {
  IDENTIFICATION_FIELDS,
  fieldDisplayValue,
} from './identification-utils';
import shell from './structuring-module.module.scss';
import styles from './identification.module.scss';

interface Props {
  documentId: number | string;
}

const toPartyStatus = (status: WellSectionStatus): PartyStatus => {
  if (status === 'complete') return 'complete';
  if (status === 'removed') return 'removed';
  if (status === 'not_started') return 'not_started';
  return 'in_progress';
};

const HEADERS = [
  { key: 'field', header: 'Field' },
  { key: 'value', header: 'Value' },
];

export const IdentificationSection: FC<Props> = ({ documentId }) => {
  const detailHref = identificationUrl(documentId);
  const detailAtom = useMemo(
    () => createIdentificationResourceAtom(detailHref),
    [detailHref]
  );
  const [detailResource, reloadDetail] = useAtom(detailAtom);
  const { loading, error, data: detail } = formatResource(detailResource);

  const confirmIdentification = useSetAtom(confirmIdentificationAtom);
  const revertIdentification = useSetAtom(revertIdentificationAtom);

  const [editOpen, setEditOpen] = useState(false);
  const [markCorrectOpen, setMarkCorrectOpen] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const status = detail?.status ?? 'not_started';
  const isReviewed = status === 'complete';

  // One Field/Value row per mapped field (column-backed + extension), driven by
  // the shared IDENTIFICATION_FIELDS map so the label set never drifts from the
  // edit panel. Missing values render as an em dash.
  const rows = useMemo(() => {
    if (!detail) return [];
    return IDENTIFICATION_FIELDS.map((def) => ({
      id: def.key,
      field: def.label,
      value: fieldDisplayValue(def, detail.fields, detail.extensionData),
    }));
  }, [detail]);

  const confirmHref = detail?.resources?.confirmation?.href;

  const handleMarkCorrect = () => {
    if (!confirmHref) return;
    setMarkCorrectOpen(false);
    setActionError(null);
    confirmIdentification({
      url: confirmHref,
      method: 'POST',
      onSuccess: () => reloadDetail(),
      onError: (m) => setActionError(m ?? 'Failed to mark as correct.'),
    });
  };

  const handleRemoveCorrect = () => {
    if (!confirmHref) return;
    setActionError(null);
    revertIdentification({
      url: confirmHref,
      method: 'DELETE',
      onSuccess: () => reloadDetail(),
      onError: (m) => setActionError(m ?? 'Failed to remove correct status.'),
    });
  };

  return (
    <div className={shell.overviewBody}>
      <div className={shell.overviewSectionTitleRow}>
        <h4 className={shell.overviewSectionTitle}>AFE Identification</h4>
        <StatusTag
          status={toPartyStatus(status)}
          progress={isReviewed ? '1/1' : '0/1'}
        />
      </div>

      {actionError && (
        <InlineNotification
          kind="error"
          lowContrast
          title="Error"
          subtitle={actionError}
          onCloseButtonClick={() => setActionError(null)}
        />
      )}
      {error && (
        <InlineNotification
          kind="error"
          lowContrast
          title="Unable to load identification"
          subtitle={error}
        />
      )}

      {loading && !detail ? (
        <Loading
          withOverlay={false}
          small
          description="Loading identification…"
        />
      ) : (
        detail && (
          <DataTable rows={rows} headers={HEADERS}>
            {({
              rows: dtRows,
              headers,
              getHeaderProps,
              getTableProps,
              onInputChange,
            }: any) => (
              <TableContainer>
                <TableToolbar size="sm">
                  <TableToolbarContent>
                    <TableToolbarSearch
                      onChange={onInputChange}
                      placeholder="Search fields…"
                      persistent
                      size="sm"
                    />
                    {/* Mock keeps Edit visible-but-disabled once reviewed and
                        flips the primary button's text/action, rather than
                        swapping the whole toolbar. KEEP in sync with the v2 mock. */}
                    <Button
                      kind="ghost"
                      size="sm"
                      renderIcon={Edit}
                      disabled={isReviewed}
                      onClick={() => setEditOpen(true)}
                    >
                      Edit
                    </Button>
                    {isReviewed ? (
                      <Button
                        kind="primary"
                        size="sm"
                        renderIcon={Close}
                        onClick={handleRemoveCorrect}
                      >
                        Remove Correct Status
                      </Button>
                    ) : (
                      <Button
                        kind="primary"
                        size="sm"
                        renderIcon={CheckmarkOutline}
                        disabled={!confirmHref}
                        onClick={() => setMarkCorrectOpen(true)}
                      >
                        Mark as Correct
                      </Button>
                    )}
                  </TableToolbarContent>
                </TableToolbar>
                <Table
                  {...getTableProps()}
                  size="sm"
                  className={styles.identTable}
                >
                  <TableHead>
                    <TableRow>
                      {headers.map((h: any) => (
                        <TableHeader
                          {...getHeaderProps({ header: h })}
                          key={h.key}
                        >
                          {h.header}
                        </TableHeader>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dtRows.map((row: any) => (
                      <TableRow key={row.id}>
                        {row.cells.map((cell: any) => (
                          <TableCell key={cell.id}>{cell.value}</TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </DataTable>
        )
      )}

      {detail && (
        <IdentificationEditPanel
          open={editOpen}
          documentId={documentId}
          detail={detail}
          onClose={() => setEditOpen(false)}
          onSaved={() => {
            setEditOpen(false);
            reloadDetail();
          }}
        />
      )}

      <Modal
        open={markCorrectOpen}
        modalHeading="Mark as Correct"
        primaryButtonText="Confirm"
        secondaryButtonText="Cancel"
        size="sm"
        onRequestClose={() => setMarkCorrectOpen(false)}
        onSecondarySubmit={() => setMarkCorrectOpen(false)}
        onRequestSubmit={handleMarkCorrect}
      >
        <p>
          Are you sure you want to mark AFE Identification as correct? This will
          confirm the extracted AFE details and disable editing.
        </p>
      </Modal>
    </div>
  );
};
